import VerifiedIcon from '@mui/icons-material/Verified';
import { IconButton, Tooltip } from '@mui/material';
import {
  GridActionsColDef,
  GridColDef,
  GridRenderCellParams,
  GridRowParams
} from '@mui/x-data-grid-premium';
import {
  useIsBottomFromTechnicianCoordinator,
  IsBottomFromTechnicianCoordinator
} from 'shared/permissions/IsBottomFromTechnicianCoordinator';
import { useIsDirector } from 'shared/permissions/useIsDirector';
import { IsSuperAdminGuard } from 'shared/permissions/IsSuperAdmin';
import { useIsSupplier } from 'shared/permissions/useIsSupplier';
import { useIsTechnician } from 'shared/permissions/useIsTechnician';
import { useIsTenantAdministrator } from 'shared/permissions/IsTenantAdministrator';
import FileUploader from 'components/ui/file-uploader/FileUploader';
import { getTicketComplianceDisplay } from 'features/inventory/ticketing/ticketCompliance';
import { StatusOption } from 'shared/types/general';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ticketingService } from 'features/inventory/ticketing/TicketingService';
import { DataGridCell, baseCell, dateTimeCell } from 'components/cells';
import {
  filterOperators,
  booleanFilterOperators,
  dateFilterOperators,
  singleSelectFilterOperators
} from 'components/enhanced';
import { ConfirmationDeleteTableDialog } from 'components/ui/feedback';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const parseTicketDateTime = (value?: string | null): Date | null => {
  if (!value) return null;
  const parsed = dayjs(value, ['DD/MM/YYYY HH:mm', 'YYYY-MM-DD HH:mm', 'YYYY-MM-DDTHH:mm'], true);
  return parsed.isValid() ? parsed.toDate() : null;
};

const TICKET_STATUS_OPTIONS_FALLBACK: StatusOption[] = [
  { value: 1, display_name: 'OPEN' },
  { value: 2, display_name: 'INTERVENTION_REQUESTED' },
  { value: 3, display_name: 'PHONE_RESOLVED' },
  { value: 4, display_name: 'TECHNICIAN_RESOLVED' },
  { value: 5, display_name: 'INTERVENTION_COMPLETED' },
  { value: 6, display_name: 'UNDETERMINED' },
  { value: 7, display_name: 'TECHNICIAN_CREATED' },
  { value: 8, display_name: 'INTERVENTION_COMPLETED_MISSING_REPORT' }
];

const TICKET_STATUS_I18N_PREFIX = 'pages.tickets.status_options';

const ticketStatusLabel = (t: (key: string) => string, statusKey: string) => {
  if (!statusKey) return '';
  const key = `${TICKET_STATUS_I18N_PREFIX}.${statusKey}`;
  const translated = t(key);
  return translated === key ? statusKey : translated;
};

const createTicketComplianceCell = (t: (key: string) => string) => {
  function TicketComplianceCellRenderer(params: GridRenderCellParams) {
    const label = getTicketComplianceDisplay(t, params.value as boolean | null);
    return <DataGridCell content={label} />;
  }
  TicketComplianceCellRenderer.displayName = 'TicketComplianceCell';
  return TicketComplianceCellRenderer;
};

export type UseTicketColumnsOptions = {
  handleApprovation?: boolean;
  approve?: (id: number, old_approvation_status?: boolean | null) => void;
  handleDelete?: (id: number) => void;
  excludeFields?: string[];
};

export function useTicketColumns({
  handleApprovation = false,
  approve,
  handleDelete,
  excludeFields = []
}: UseTicketColumnsOptions = {}): GridColDef[] {
  const translationPrefix = 'entities.equipment';
  const { t } = useTranslation();
  const isDisableEdit = useIsDirector() || useIsTechnician() || useIsSupplier();
  /** Data/ora editabili in griglia solo per amministratori; per i tecnici sono auto-generate */
  const canEditDates = useIsTenantAdministrator();
  const [statusOptions, setStatusOptions] = useState<StatusOption[]>([]);
  const isSuperAdmin = IsSuperAdminGuard();
  const isTenantAdmin = useIsTenantAdministrator();
  const canDelete =
    isSuperAdmin || isTenantAdmin || useIsBottomFromTechnicianCoordinator() || useIsTechnician();
  const excludeSet = useMemo(() => new Set(excludeFields), [excludeFields]);

  useEffect(() => {
    ticketingService
      .options()
      .then((res) => {
        const raw =
          res?.actions?.POST?.status?.choices ??
          TICKET_STATUS_OPTIONS_FALLBACK.filter((c) => c.value !== 0);
        setStatusOptions(raw);
      })
      .catch(() => {
        setStatusOptions(TICKET_STATUS_OPTIONS_FALLBACK.filter((c) => c.value !== 0));
      });
  }, []);

  return useMemo(() => {
    const columns: GridColDef[] = [
      {
        field: 'id',
        headerName: t(`global.number`),
        width: 50,
        filterOperators,
        renderCell: (params: GridRenderCellParams) => (
          <div>
            <Link to={`/tenant/equipments/ticketing/${params.value}`}>
              <b className="text-secondary-500">#{params.value}</b>
            </Link>
          </div>
        )
      }
    ];

    if (handleApprovation) {
      columns.push({
        field: 'approvation_status',
        headerName: t(`global.approvation-status`),
        width: 75,
        sortable: false,
        filterOperators: booleanFilterOperators,
        renderCell: (params: GridRenderCellParams) => (
          <div>
            {isDisableEdit ? (
              <VerifiedIcon
                color={params.row.approvation_status === true ? 'success' : 'disabled'}
              />
            ) : (
              <Tooltip
                title={t('actions.approve', { user: params.row.approvation_user })}
                placement="top">
                <IconButton
                  size="small"
                  aria-label={t('actions.approve')}
                  onClick={() => approve?.(params.row.id, params.row.approvation_status)}>
                  <VerifiedIcon
                    color={params.row.approvation_status === true ? 'success' : 'disabled'}
                  />
                </IconButton>
              </Tooltip>
            )}
          </div>
        )
      });
    }

    columns.push(
      {
        field: 'equipment',
        headerName: t(`global.equipment`),
        width: 200,
        filterOperators,
        renderCell: baseCell
      },
      {
        field: 'equipment_description',
        headerName: t(`${translationPrefix}.equipment_description`),
        width: 200,
        filterOperators,
        renderCell: baseCell
      },
      {
        field: 'accessor_code',
        headerName: t(`global.accessor-code`),
        width: 200,
        filterOperators,
        renderCell: baseCell
      },
      {
        field: 'accessor_description',
        headerName: t(`global.accessor-description`),
        width: 200,
        filterOperators,
        renderCell: baseCell
      },
      {
        field: 'serial_number',
        headerName: t(`${translationPrefix}.serial_number`),
        width: 200,
        filterOperators,
        renderCell: baseCell
      },
      {
        field: 'description',
        headerName: 'Problema Riscontrato',
        width: 260,
        filterOperators,
        sortable: false,
        renderCell: baseCell
      },
      {
        field: 'note',
        headerName: t(`global.notes`),
        width: 220,
        filterOperators,
        sortable: false,
        renderCell: baseCell
      },
      {
        field: 'notes',
        headerName: t(`global.notes`),
        width: 220,
        filterOperators,
        sortable: false,
        valueGetter: (params) => params.row?.notes ?? params.row?.note ?? '',
        renderCell: baseCell
      },
      {
        field: 'warehouse_code',
        headerName: t(`global.warehouse`),
        width: 150,
        filterOperators,
        renderCell: baseCell
      },
      {
        field: 'position',
        headerName: t(`entities.equipment.position`),
        width: 200,
        filterOperators,
        renderCell: baseCell
      },
      {
        field: 'supplier',
        headerName: t(`global.supplier`),
        width: 200,
        filterOperators,
        renderCell: baseCell
      },
      {
        field: 'created_at',
        headerName: t(`entities.tickets.created_at`),
        width: 200,
        filterOperators: dateFilterOperators,
        editable: canEditDates,
        type: 'dateTime',
        valueGetter: ({ value }) => parseTicketDateTime(value),
        renderCell: dateTimeCell
      },
      {
        field: 'code_error',
        headerName: t(`${translationPrefix}.ticket_code_error`),
        width: 150,
        filterOperators,
        renderCell: baseCell
      },
      {
        field: 'creation_user',
        headerName: t(`entities.tickets.creation_user`),
        width: 200,
        filterOperators,
        renderCell: baseCell
      },
      {
        field: 'sent_at',
        headerName: t(`entities.tickets.sent_at`),
        width: 200,
        filterOperators: dateFilterOperators,
        editable: canEditDates,
        type: 'dateTime',
        valueGetter: ({ value }) => parseTicketDateTime(value),
        renderCell: dateTimeCell
      },
      {
        field: 'approvation_user',
        headerName: t(`entities.tickets.approvation_user`),
        width: 200,
        filterOperators,
        renderCell: baseCell
      },
      {
        field: 'resolved_at',
        headerName: t(`entities.tickets.resolved_at`),
        width: 200,
        filterOperators: dateFilterOperators,
        editable: canEditDates,
        type: 'dateTime',
        valueGetter: ({ value }) => parseTicketDateTime(value),
        renderCell: dateTimeCell
      },
      {
        field: 'specialist_arrival_at',
        headerName: 'Arrivo tecnico specialista (data/ora)',
        width: 200,
        filterOperators: dateFilterOperators,
        editable: canEditDates,
        type: 'dateTime',
        valueGetter: ({ value }) => parseTicketDateTime(value),
        renderCell: dateTimeCell
      },
      {
        field: 'status',
        headerName: t(`global.status`),
        type: 'singleSelect',
        width: 100,
        valueGetter: (params) => {
          const r = params.row;
          if (r.status?.value != null) return r.status.value;
          const name = r.status_display;
          const opt = TICKET_STATUS_OPTIONS_FALLBACK.find((c) => c.display_name === name);
          return opt?.value ?? name;
        },
        valueOptions: statusOptions.map((type) => ({
          label: ticketStatusLabel(t, type.display_name),
          value: type.value
        })),
        filterOperators: singleSelectFilterOperators,
        renderCell: (params: GridRenderCellParams) => (
          <div>
            {ticketStatusLabel(
              t,
              params.row.status?.display_name ?? params.row.status_display ?? ''
            )}
          </div>
        )
      },
      {
        field: 'compliant',
        headerName: t(`global.compliant`),
        width: 100,
        filterOperators: booleanFilterOperators,
        editable: !isDisableEdit,
        type: 'boolean',
        valueFormatter: (params) => getTicketComplianceDisplay(t, params.value as boolean | null),
        renderCell: createTicketComplianceCell(t)
      },
      {
        field: 'technical_report',
        width: 70,
        headerName: t(`${translationPrefix}.technical_report`),
        renderCell: (params: GridRenderCellParams) => {
          const ticket = params.row;
          return (
            <IsBottomFromTechnicianCoordinator>
              <div>
                <FileUploader
                  service={ticketingService}
                  entityId={ticket.id}
                  attachmentName={'technical_report'}
                  filename={ticket.technical_report}
                />
              </div>
            </IsBottomFromTechnicianCoordinator>
          );
        },
        filterable: false,
        sortable: false
      }
    );
    if (handleDelete && isSuperAdmin) {
      columns.push({
        field: 'actions',
        type: 'actions',
        headerName: t(`global.actions`),
        getActions: (params: GridRowParams) => {
          return [
            <ConfirmationDeleteTableDialog
              key={`delete-${params.row.id}`}
              handleDelete={handleDelete}
              id={params.row.id}
            />
          ];
        }
      } as GridActionsColDef);
    }

    if (excludeSet.size === 0) {
      return columns;
    }
    return columns.filter((col) => !excludeSet.has(col.field));
  }, [
    t,
    handleApprovation,
    approve,
    handleDelete,
    statusOptions,
    isDisableEdit,
    canEditDates,
    canDelete,
    excludeSet
  ]);
}
